import type { ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { onNoRole } from '~/lib/api';
import { createAuth } from '~/lib/auth';
import type { Account, AuthError } from '~/lib/auth.common';
import { describeAuthError } from '~/lib/auth.common';
import { logger } from '~/lib/logger';

export type { AuthError } from '~/lib/auth.common';

/**
 * Auth state (D20, 18.12): Microsoft Entra, and nothing else.
 *
 * The shape is unchanged from the Firebase version on purpose — `status`, `signIn`,
 * `signOut`, `error`, `busy` — so no screen but the sign-in one had to change. What did
 * change is that `signIn()` takes NO arguments: the app no longer sees an email or a
 * password, because the provider owns the credential prompt. An app that cannot read a
 * password cannot leak one.
 */
export type AuthState =
  | { status: 'loading' }
  | { status: 'unauthenticated' }
  /**
   * Signed in with Microsoft, and not an operator here (frame 00c).
   *
   * A distinct status rather than a flag on `authenticated`, because the two must not
   * share a screen: this person has a valid session and no route into the app, and every
   * action on the operator home would 403. `user` is carried so 00c can show WHO is
   * signed in — an operator handed a shared handset needs to see whose account it is
   * before being told to go and ask an administrator about it.
   */
  | { status: 'no_access'; user: Account }
  | { status: 'authenticated'; user: Account };

export type AuthContextValue = AuthState & {
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  error: AuthError | null;
  busy: boolean;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });
  const [error, setError] = useState<AuthError | null>(null);
  const [busy, setBusy] = useState(false);

  /*
    One backend for the life of the provider. MSAL holds a redirect promise and a token
    cache, and AppAuth holds the keystore session; building a second one per render would
    mean two caches disagreeing about who is signed in.
  */
  const backend = useRef<ReturnType<typeof createAuth> | null>(null);
  backend.current ??= createAuth();

  useEffect(() => {
    let cancelled = false;
    /*
      `init()` finishes the redirect. On web the app is re-entered after the provider sends
      the browser back, and until this resolves there is no account — which is why the
      initial status is `loading` rather than `unauthenticated`. Showing the sign-in screen
      first would flash it at an operator who has just signed in.
    */
    backend.current
      ?.init()
      .then((account) => {
        if (cancelled) return;
        setState(
          account ? { status: 'authenticated', user: account } : { status: 'unauthenticated' },
        );
      })
      .catch((e) => {
        if (cancelled) return;
        logger.error('[auth] init failed', e);
        setError(describeAuthError(e));
        setState({ status: 'unauthenticated' });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const account = await backend.current?.signIn();
      /*
        Null on web, and not an error: `loginRedirect` resolves once the redirect is under
        way and the page is about to unload, so the account arrives through `init()` on the
        way back. Native returns it directly, having waited for Custom Tabs.
      */
      if (account) setState({ status: 'authenticated', user: account });
    } catch (e) {
      /*
        The operator gets our words; the log keeps the provider's. Without this a failed
        sign-in leaves "Couldn't sign you in" on screen and NOTHING anywhere else — which
        is exactly how much there was to go on the first time MSAL refused a
        misconfigured authority.
      */
      logger.error('[auth] sign-in failed', e);
      setError(describeAuthError(e));
      throw e;
    } finally {
      setBusy(false);
    }
  }, []);

  /*
    The role refusal arrives from the API, not from the provider: Entra signs this person
    in perfectly happily, and only our service knows they hold no `Glass.Operator`. So it
    reaches auth state through the one choke point every request passes.

    Guarded on `user` because a refusal can only describe somebody — and gated on the
    CURRENT status so a stale 403 landing after a sign-out cannot resurrect a session.
  */
  useEffect(() => {
    onNoRole(() => {
      setState((prev) =>
        prev.status === 'authenticated' ? { ...prev, status: 'no_access' } : prev,
      );
    });
    return () => onNoRole(null);
  }, []);

  const signOut = useCallback(async () => {
    setError(null);
    try {
      await backend.current?.signOut();
    } finally {
      // Whatever the provider did, this device is signed out. A failed end-session must not
      // leave an operator signed in on a handset they have just handed over.
      setState({ status: 'unauthenticated' });
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, error, busy, signIn, signOut }),
    [state, error, busy, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside AuthProvider');
  }
  return ctx;
}
